import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

/**
 * Page-level + section-level tabs primitive.
 *
 * Source: docs/engineering/contracts/UI_COMPONENT_CONTRACT.md L21
 * (IA-REDESIGN 2026-05-07; ships under BRIEF_ia-redesign-frontend-implementation
 * § 4.1).
 *
 * ARIA contract:
 * - Container: role="tablist"
 * - Each tab: role="tab", aria-selected, aria-controls, tabIndex per
 *   WAI-ARIA Authoring Practices (selected tab = 0; others = -1).
 * - Arrow keys traverse tablist; Home / End jump to first / last.
 * - The optional `chip` slot renders a separate dropdown adjacent to the
 *   tablist (NOT inside the tablist semantically).
 */

export type TabValue = string;

export interface TabsTabItem {
  value: TabValue;
  label: string;
  count?: number;
}

export interface TabsChipOption {
  value: string;
  label: string;
  count?: number;
}

export interface TabsChip {
  options: TabsChipOption[];
  value: string;
  onValueChange: (next: string) => void;
  label?: string;
}

interface TabsProps {
  value: TabValue;
  onValueChange: (next: TabValue) => void;
  tabs: TabsTabItem[];
  chip?: TabsChip;
  className?: string;
  ariaLabel?: string;
  children?: React.ReactNode;
}

export default function Tabs({
  value,
  onValueChange,
  tabs,
  chip,
  className,
  ariaLabel,
  children,
}: TabsProps) {
  const { t } = useTranslation();
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // Reset ref array length when tabs change.
  useEffect(() => {
    tabRefs.current = tabRefs.current.slice(0, tabs.length);
  }, [tabs.length]);

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    let nextIndex: number;
    if (event.key === "ArrowRight") {
      nextIndex = (index + 1) % tabs.length;
    } else if (event.key === "ArrowLeft") {
      nextIndex = (index - 1 + tabs.length) % tabs.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = tabs.length - 1;
    } else {
      return;
    }
    event.preventDefault();
    onValueChange(tabs[nextIndex].value);
    tabRefs.current[nextIndex]?.focus();
  };

  const activePanelId = `tabpanel-${value}`;

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <div className="flex items-end justify-between gap-4 border-b border-divider">
        <div
          role="tablist"
          aria-label={ariaLabel ?? t("ui.tabs.defaultAriaLabel")}
          className="flex items-end gap-1"
        >
          {tabs.map((tab, index) => {
            const isSelected = tab.value === value;
            return (
              <button
                key={tab.value}
                ref={(el) => {
                  tabRefs.current[index] = el;
                }}
                role="tab"
                type="button"
                id={`tab-${tab.value}`}
                aria-selected={isSelected}
                aria-controls={`tabpanel-${tab.value}`}
                tabIndex={isSelected ? 0 : -1}
                onClick={() => onValueChange(tab.value)}
                onKeyDown={(event) => handleKeyDown(event, index)}
                className={cn(
                  "relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
                  isSelected
                    ? "text-primary-text"
                    : "text-ink-soft hover:text-ink",
                )}
              >
                <span>{tab.label}</span>
                {typeof tab.count === "number" && (
                  <span
                    className={cn(
                      "text-xs font-normal",
                      isSelected ? "text-primary-dark" : "text-muted",
                    )}
                  >
                    {tab.count}
                  </span>
                )}
                {isSelected && (
                  <span
                    aria-hidden="true"
                    className="absolute inset-x-2 -bottom-px h-0.5 rounded-t-full bg-primary-500"
                  />
                )}
              </button>
            );
          })}
        </div>
        {chip && (
          <TabsChipDropdown
            options={chip.options}
            value={chip.value}
            onValueChange={chip.onValueChange}
            label={chip.label ?? t("ui.tabs.chipDefaultLabel")}
          />
        )}
      </div>
      <div
        role="tabpanel"
        id={activePanelId}
        aria-labelledby={`tab-${value}`}
        tabIndex={0}
        // A tab stop needs a visible focus indicator (WCAG 2.4.7).
        className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
      >
        {children}
      </div>
    </div>
  );
}

interface TabsChipDropdownProps {
  options: TabsChipOption[];
  value: string;
  onValueChange: (next: string) => void;
  label: string;
}

function TabsChipDropdown({
  options,
  value,
  onValueChange,
  label,
}: TabsChipDropdownProps) {
  const { t } = useTranslation();
  const selected = options.find((opt) => opt.value === value);
  return (
    <label className="flex items-center gap-2 pb-2 text-xs font-medium text-ink-soft">
      <span>{label}:</span>
      <select
        value={value}
        onChange={(event) => onValueChange(event.target.value)}
        className={cn(
          "rounded-lg border border-divider bg-surface px-2 py-1 text-sm font-medium text-ink-soft",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
        )}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
            {typeof opt.count === "number" ? ` (${opt.count})` : ""}
          </option>
        ))}
      </select>
      {selected && typeof selected.count === "number" && (
        <span className="sr-only">
          {t("ui.tabs.chipSelectedItemsCount", { count: selected.count })}
        </span>
      )}
    </label>
  );
}
